import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { createSelector } from "reselect";
import type { Case } from "@/types";
import api from "@/services/api";

export interface CaseFilter {
  status?: Case["status"];
  debtorId?: string;
  agentId?: string;
  searchTerm?: string;
}

export interface CaseSort {
  field: keyof Case | null;
  direction: "asc" | "desc";
}

interface CasesState {
  items: Case[];
  loading: boolean;
  error: string | null;
  filter: CaseFilter;
  sort: CaseSort;
  currentPage: number;
  pageSize: number;
  total: number;
}

const initialState: CasesState = {
  items: [],
  loading: false,
  error: null,
  filter: {},
  sort: { field: null, direction: "asc" },
  currentPage: 1,
  pageSize: 10,
  total: 0,
};

export const fetchCases = createAsyncThunk(
  "cases/fetchCases",
  async (params: { page?: number; per_page?: number; searchTerm?: string } = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { cases: CasesState };
      const { currentPage, pageSize, filter } = state.cases;
      
      const queryParams = new URLSearchParams({
        page: String(params?.page ?? currentPage),
        per_page: String(params?.per_page ?? pageSize),
        ...(params?.searchTerm ?? filter.searchTerm) && { search: params?.searchTerm ?? filter.searchTerm },
      });

      const response = await api.get<{
        data: Case[];
        meta: {
          current_page: number;
          per_page: number;
          total: number;
          last_page: number;
          from: number | null;
          to: number | null;
        };
      }>(`/v1/cases?${queryParams}`);
      
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || "Failed to fetch cases");
    }
  }
);

const casesSlice = createSlice({
  name: "cases",
  initialState,
  reducers: {
    setCaseFilter: (state, action: PayloadAction<CaseFilter>) => {
      state.filter = action.payload;
      state.currentPage = 1;
    },
    setCaseSort: (state, action: PayloadAction<CaseSort>) => {
      state.sort = action.payload;
      state.currentPage = 1;
    },
    setCasePage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setCasePageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1;
    },
    resetCaseFilters: (state) => {
      state.filter = {};
      state.sort = { field: null, direction: "asc" };
      state.currentPage = 1;
    },
    addCase: (state, action: PayloadAction<Case>) => {
      state.items.unshift(action.payload);
      state.total += 1;
    },
    updateCase: (state, action: PayloadAction<Case>) => {
      const index = state.items.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        state.items[index] = action.payload;
      }
    },
    deleteCase: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((c) => c.id !== action.payload);
      state.total -= 1;
    },
    resetCases: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCases.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCases.fulfilled, (state, action) => {
        state.loading = false;
        state.items = Array.isArray(action.payload.data) ? action.payload.data : [];
        state.total = action.payload.meta?.total ?? 0;
        state.currentPage = action.payload.meta?.current_page ?? 1;
        state.pageSize = action.payload.meta?.per_page ?? state.pageSize;
        state.error = null;
      })
      .addCase(fetchCases.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setCaseFilter,
  setCaseSort,
  setCasePage,
  setCasePageSize,
  resetCaseFilters,
  addCase,
  updateCase,
  deleteCase,
  resetCases,
} = casesSlice.actions;
export default casesSlice.reducer;

// Selectors
export const selectCases = (state: any) => state.cases.items;
export const selectCasesLoading = (state: any) => state.cases.loading;
export const selectCasesError = (state: any) => state.cases.error;
export const selectCaseFilter = (state: any) => state.cases.filter;
export const selectCaseSort = (state: any) => state.cases.sort;
export const selectCasePagination = (state: any) => ({
  currentPage: state.cases.currentPage,
  pageSize: state.cases.pageSize,
  total: state.cases.total,
});

export const selectFilteredCases = createSelector(
  [(state: any) => state.cases],
  (state) => ({
    data: state.items,
    total: state.total,
    currentPage: state.currentPage,
    pageSize: state.pageSize,
    hasNextPage: state.currentPage < Math.ceil(state.total / state.pageSize),
    hasPrevPage: state.currentPage > 1,
  }),
);
